import type { Router } from 'vue-router'

import type { MenuRecordRaw } from '@vben/types'

import { LOGIN_PATH } from '@vben/constants'
import { preferences } from '@vben/preferences'
import { useAccessStore, useUserStore } from '@vben/stores'
import { startProgress, stopProgress } from '@vben/utils'

import { authApi } from '#/api'
import { isInMicroApp } from '#/micro-app'
import { accessRoutes, coreRouteNames } from '#/router/routes'
import { useAuthStore } from '#/store'

import { generateAccess } from './access'

/**
 * 通用守卫配置
 * @param router
 */
function setupCommonGuard(router: Router) {
  // 记录已经加载的页面
  const loadedPaths = new Set<string>()

  router.beforeEach(async (to) => {
    to.meta.loaded = loadedPaths.has(to.path)

    // 页面加载进度条
    // 开启keepalive时只在第一次加载时显示进度条
    if (
      preferences.transition.progress &&
      (!to.meta.keepAlive || !to.meta.loaded)
    ) {
      startProgress()
    }
    return true
  })

  router.afterEach((to) => {
    // 记录页面是否加载,如果已经加载，后续的页面切换动画等效果不在重复执行

    loadedPaths.add(to.path)

    // 关闭页面加载进度条
    if (preferences.transition.progress) {
      stopProgress()
    }
  })
}

/**
 * 权限访问守卫配置
 * @param router
 */
function setupAccessGuard(router: Router) {
  router.beforeEach(async (to, from) => {
    const accessStore = useAccessStore()
    const userStore = useUserStore()
    const authStore = useAuthStore()

    // 基本路由，这些路由不需要进入权限拦截
    if (coreRouteNames.includes(to.name as string)) {
      if (to.path === LOGIN_PATH && accessStore.accessToken) {
        return decodeURIComponent(
          (to.query?.redirect as string) ||
            userStore.userInfo?.homePath ||
            preferences.app.defaultHomePath,
        )
      }
      return true
    }

    // 检查地址栏是否有token参数，如果有则使用它
    if (to.query.token && typeof to.query.token === 'string') {
      accessStore.setAccessToken(to.query.token)
      // 移除地址栏中的token参数，避免token暴露在URL中
      const newQuery = { ...to.query }
      delete newQuery.token
    }

    // accessToken 检查
    if (!accessStore.accessToken) {
      // 明确声明忽略权限访问权限，则可以访问
      if (to.meta.ignoreAccess) {
        return true
      }

      // 没有访问权限，跳转登录页面
      if (to.fullPath !== LOGIN_PATH) {
        return {
          path: LOGIN_PATH,
          // 如不需要，直接删除 query
          query:
            to.fullPath === preferences.app.defaultHomePath
              ? {}
              : { redirect: encodeURIComponent(to.fullPath) },
          // 携带当前跳转的页面，登录后重新跳转该页面
          replace: true,
        }
      }
      return to
    }

    // 是否已经生成过动态路由
    if (accessStore.isAccessChecked) {
      return true
    }

    // 生成路由表
    // 当前登录用户拥有的角色标识列表
    const userInfo = userStore.userInfo || (await authStore.fetchUserInfo())
    const userRoles = userInfo?.roles ?? []

    // 生成菜单和路由
    const { accessibleMenus, accessibleRoutes } = await generateAccess({
      roles: userRoles,
      router,
      // 则会在菜单中显示，但是访问会被重定向到403
      routes: accessRoutes,
    })

    // 保存菜单信息和路由信息
    if (!isInMicroApp()) {
      console.log('isInMicroApp')
      const { authCodes, paths } = await authApi.getAccessCodes()
      accessStore.setAccessCodes(authCodes)
      const filterMenusByPaths = (menus: MenuRecordRaw[]) => {
        return menus
          .map((item) => {
            const children: any =
              item.children && item.children.length > 0
                ? filterMenusByPaths(item.children)
                : []
            return { ...item, children }
          })
          .filter(
            (item) =>
              paths.includes(item.path) ||
              (item.children && item.children.length > 0),
          )
      }
      const filteredMenus = filterMenusByPaths(accessibleMenus)
      accessStore.setAccessMenus(filteredMenus)
    }

    accessStore.setAccessRoutes(accessibleRoutes)
    accessStore.setIsAccessChecked(true)
    const redirectPath = (from.query.redirect ??
      (to.path === preferences.app.defaultHomePath
        ? userInfo?.homePath || preferences.app.defaultHomePath
        : to.fullPath)) as string

    return {
      ...router.resolve(decodeURIComponent(redirectPath)),
      replace: true,
    }
  })
}

/**
 * 项目守卫配置
 * @param router
 */
function createRouterGuard(router: Router) {
  /** 通用 */
  setupCommonGuard(router)
  /** 权限访问 */
  setupAccessGuard(router)
}

export { createRouterGuard }
